import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Globe, Loader2 } from 'lucide-react';
import { domains } from '../../services/superuserApi';

// Alvos de toque ≥ 44 px e texto ≥ 12 px: este painel é usado no telemóvel, a 390 px.
const TAP = 44;

const STATUS_STYLE = {
  Created: { background: 'var(--success-bg, #e7f6ec)', color: 'var(--success-text, #146c2e)' },
  Failed: { background: 'var(--error-bg, #fdeaea)', color: 'var(--error-text, #a11616)' },
  Pending: { background: 'var(--bg-tertiary, #f1f1f1)', color: 'var(--text-muted, #6b6b6b)' },
};

const fmtDate = (value) => (value
  ? new Date(value).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' })
  : null);

const apiMessage = (e, fallback) => e?.response?.data?.message || e?.message || fallback;

/**
 * O mesmo que o servidor valida, para o erro aparecer antes de um pedido inútil.
 * Só o domínio: sem esquema, sem caminho, sem porta, em minúsculas.
 */
export const domainProblem = (value) => {
  const raw = (value || '').trim();
  if (!raw) return 'Indique o domínio (ex.: ginasio.exemplo.pt).';
  if (raw.includes('://')) return 'Escreva só o domínio, sem esquema (sem https://).';
  if (raw.includes('/')) return 'Escreva só o domínio, sem caminho.';
  if (raw.includes(':')) return 'Escreva só o domínio, sem porta.';
  if (/\s/.test(raw)) return 'O domínio não pode ter espaços nem parâmetros.';
  if (/[A-Z]/.test(raw)) return 'Use o domínio em minúsculas (ex.: ginasio.exemplo.pt).';
  if (!/^[a-z0-9.-]+$/.test(raw)) return 'O domínio só pode ter letras minúsculas, dígitos, pontos e hífenes.';
  if (raw.startsWith('.') || raw.endsWith('.') || raw.includes('..')) {
    return 'O domínio tem pontos a mais (nem no início, nem no fim, nem seguidos).';
  }
  const labels = raw.split('.');
  if (labels.length < 2) return 'O domínio tem de ter um ponto (ex.: ginasio.exemplo.pt).';
  if (labels.some((l) => l.length === 0 || l.length > 63)) {
    return 'Cada parte do domínio tem de ter entre 1 e 63 caracteres.';
  }
  if (labels.some((l) => l.startsWith('-') || l.endsWith('-'))) {
    return 'Cada parte do domínio não pode começar nem acabar com hífen.';
  }
  const tld = labels[labels.length - 1];
  if (tld.length < 2 || !/^[a-z]+$/.test(tld)) {
    return 'O domínio tem de acabar numa extensão com pelo menos duas letras (ex.: .pt).';
  }
  return null;
};

/**
 * Domínio e certificado HTTPS de UMA instância, através do Nginx Proxy Manager.
 *
 * Duas coisas que este painel NÃO faz, e diz:
 *  - sem `Npm:BaseUrl` e `Npm:CredentialsFile` no servidor não há integração: mostra o aviso e
 *    não oferece um botão que ia falhar; as instruções manuais continuam em Ajuda;
 *  - nunca diz «criado» sobre uma falha: o estado e o motivo vêm do servidor, e uma falha do
 *    NPM aparece como erro, com o que ele respondeu.
 *
 * O DNS do domínio tem de apontar para o servidor ANTES de pedir o certificado: o Let's Encrypt
 * valida o domínio por HTTP. O painel di-lo antes do botão.
 */
const DomainPanel = ({ instanceId, instanceName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await domains.detail(instanceId);
      const state = res.data?.domain || null;
      setData({ configured: !!res.data?.configured, state, message: res.data?.message });
      if (state?.domain && !value) setValue(state.domain);
    } catch (e) {
      setLoadError(apiMessage(e, 'Não foi possível ler o domínio desta instância.'));
    } finally {
      setLoading(false);
    }
    // `value` fica de fora de propósito: não pode apagar o que está a ser escrito.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  useEffect(() => { load(); }, [load]);

  const configured = !!data?.configured;
  const state = data?.state;
  const status = state?.status || 'Pending';
  const expires = fmtDate(state?.certificateExpiresAt);

  const create = async () => {
    const local = domainProblem(value);
    if (local) {
      setProblem(local);
      return;
    }

    setBusy(true);
    setProblem(null);
    try {
      const res = await domains.ensure(instanceId, value.trim());
      toast.success(res.data?.message || 'Domínio e certificado criados.');
      await load();
    } catch (e) {
      // O servidor devolve o motivo (domínio inválido, NPM recusou, sem configuração). Não se
      // substitui por uma mensagem genérica: é isso que evita ir aos logs.
      setProblem(apiMessage(e, 'Não foi possível criar o domínio e o certificado.'));
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="domain-panel" style={styles.card}>
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
          <Globe size={18} color="var(--accent-primary)" style={{ flexShrink: 0 }} />
          <h3 style={styles.title}>Domínio e certificado HTTPS</h3>
        </div>
        {!loading && !loadError && configured && (
          <span data-testid="domain-status" style={{ ...styles.chip, ...(STATUS_STYLE[status] || STATUS_STYLE.Pending) }}>
            {state?.statusLabel || 'por criar'}
          </span>
        )}
      </div>

      {loading && (
        <div style={styles.muted}>
          <Loader2 size={16} style={{ animation: 'su-spin 1s linear infinite' }} /> A ler o domínio…
        </div>
      )}

      {!loading && loadError && (
        <div style={styles.errorBox}>
          <div>{loadError}</div>
          <button type="button" onClick={load} style={{ ...styles.secondary, marginTop: '0.5rem' }}>
            Tentar de novo
          </button>
        </div>
      )}

      {!loading && !loadError && !configured && (
        <div data-testid="domain-not-configured" style={styles.warningBox}>
          <strong>Integração de domínios não configurada.</strong>
          <div style={{ marginTop: '0.25rem' }}>
            O portal não tem acesso ao Nginx Proxy Manager (falta <code style={styles.code}>Npm:BaseUrl</code> ou
            o ficheiro de credenciais). O domínio e o certificado fazem-se à mão, como até aqui:
            Ajuda → <strong>Nginx Proxy Manager</strong>.
          </div>
        </div>
      )}

      {!loading && !loadError && configured && (
        <>
          {state?.publicUrl && (
            <div data-testid="domain-public-url" style={styles.publicUrl}>
              Endereço público: <strong>{state.publicUrl}</strong>
            </div>
          )}

          {status === 'Created' && expires && (
            <div data-testid="domain-certificate" style={styles.certLine}>
              {state?.certificateValid
                ? `Certificado válido até ${expires}.`
                : `Certificado expirado em ${expires} — pedir de novo atualiza-o.`}
            </div>
          )}

          {status === 'Failed' && state?.message && (
            <div data-testid="domain-message" style={styles.errorBox}>{state.message}</div>
          )}

          <label htmlFor={`domain-${instanceId}`} style={styles.label}>
            Domínio deste ginásio
          </label>
          <div style={styles.row}>
            <input
              id={`domain-${instanceId}`}
              data-testid="domain-input"
              type="text"
              inputMode="url"
              autoComplete="off"
              spellCheck={false}
              placeholder="ginasio.exemplo.pt"
              aria-label={`Domínio de ${instanceName}`}
              value={value}
              onChange={(e) => { setValue(e.target.value); setProblem(null); }}
              disabled={busy}
              style={styles.input}
            />
            <button
              type="button"
              data-testid="domain-create"
              onClick={create}
              disabled={busy}
              style={{ ...styles.primary, opacity: busy ? 0.6 : 1 }}
            >
              {busy ? 'A criar…' : 'Criar domínio e certificado'}
            </button>
          </div>

          {problem && <div data-testid="domain-error" style={styles.errorBox}>{problem}</div>}

          <div style={styles.hint}>
            Só o domínio, em minúsculas (ex.: <code style={styles.code}>ginasio.exemplo.pt</code>). O DNS tem de
            já apontar para este servidor: o certificado é validado por HTTP. Repetir não duplica nada — atualiza o
            proxy-host e reaproveita o certificado se ainda for válido.
          </div>
        </>
      )}
    </div>
  );
};

const styles = {
  card: {
    background: 'var(--bg-secondary, #fff)',
    border: '1px solid var(--border, #e3e3e3)',
    borderRadius: '16px',
    padding: '1rem',
    marginTop: '1rem',
    color: 'var(--text-primary, #1c1c1c)',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    gap: '0.75rem', flexWrap: 'wrap',
  },
  title: { margin: 0, fontSize: '1rem', fontWeight: 700 },
  chip: { padding: '2px 8px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700 },
  muted: {
    marginTop: '0.75rem', fontSize: '0.8125rem', color: 'var(--text-muted, #6b6b6b)',
    display: 'flex', alignItems: 'center', gap: '0.4rem',
  },
  warningBox: {
    marginTop: '0.75rem', padding: '0.75rem', borderRadius: '10px', fontSize: '0.8125rem',
    background: 'var(--warning-bg, #fff7e0)', color: 'var(--warning-text, #7a5200)', lineHeight: 1.6,
  },
  errorBox: {
    marginTop: '0.75rem', padding: '0.75rem', borderRadius: '10px', fontSize: '0.8125rem',
    background: 'var(--error-bg, #fdeaea)', color: 'var(--error-text, #a11616)', lineHeight: 1.5,
  },
  publicUrl: { marginTop: '0.75rem', fontSize: '0.875rem', wordBreak: 'break-word' },
  certLine: { marginTop: '0.25rem', fontSize: '0.8125rem', color: 'var(--text-muted, #6b6b6b)' },
  label: { display: 'block', marginTop: '0.75rem', marginBottom: '0.35rem', fontSize: '0.8125rem', fontWeight: 600 },
  row: { display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' },
  input: {
    flex: '1 1 220px', minWidth: 0, minHeight: TAP, padding: '0 0.75rem', borderRadius: '10px',
    border: '1px solid var(--border, #e3e3e3)', background: 'var(--bg-tertiary, #f7f7f7)',
    color: 'var(--text-primary, #1c1c1c)', fontSize: '1rem',
  },
  primary: {
    minHeight: TAP, minWidth: TAP, padding: '0 1rem', borderRadius: '10px', border: 'none',
    background: 'var(--accent-primary, #d4a017)', color: '#fff', fontWeight: 700, fontSize: '0.875rem',
    cursor: 'pointer',
  },
  secondary: {
    minHeight: TAP, padding: '0 1rem', borderRadius: '10px',
    border: '1px solid var(--border, #e3e3e3)', background: 'transparent',
    color: 'var(--text-primary, #1c1c1c)', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
  },
  hint: { marginTop: '0.625rem', fontSize: '0.75rem', color: 'var(--text-muted, #6b6b6b)', lineHeight: 1.6 },
  code: { fontFamily: 'monospace', fontSize: '0.75rem' },
};

export default DomainPanel;
